import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import EmployeeLayout from '../../components/layout/EmployeeLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  GraduationCap,
  CalendarPlus,
  Calendar,
  Trash2,
  Edit2,
  X as XIcon,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, differenceInDays, isBefore, isAfter } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const todayStr = () => format(new Date(), 'yyyy-MM-dd');

// Compliance-focused duration: always plain days, inclusive of both ends.
// "33 days" reads more clearly than "1mo 2d" when the figure is consumed
// by HR/compliance reviewers rather than shift planners.
const durationDays = (start, end) => {
  try {
    const s = parseISO(start);
    const e = parseISO(end);
    return differenceInDays(e, s) + 1;
  } catch {
    return 0;
  }
};

// Status palette per the new spec:
//   Green  → Upcoming     (hasn't started yet)
//   Amber  → On Break Now (today within [start, end])
//   Grey   → Completed    (end is in the past)
const statusFor = (start, end) => {
  try {
    const s = parseISO(start);
    const e = parseISO(end);
    const now = new Date();
    if (now >= s && now <= e) {
      return { label: 'On Break Now', class: 'bg-amber-500/20 text-amber-400 border-amber-500/40' };
    }
    if (isBefore(now, s)) {
      return { label: 'Upcoming', class: 'bg-green-500/20 text-green-400 border-green-500/40' };
    }
    if (isAfter(now, e)) {
      return { label: 'Completed', class: 'bg-slate-500/20 text-slate-400 border-slate-500/40' };
    }
    return { label: '', class: '' };
  } catch {
    return { label: '', class: '' };
  }
};

export default function EmployeeAcademicBreaksPage() {
  const [breaks, setBreaks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [form, setForm] = useState({ start_date: '', end_date: '' });
  // Mandatory accuracy confirmation — required before Save/Update can fire.
  // Resets every time the dialog opens to avoid stale consent.
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  // Inline confirmation for delete — replaces the legacy window.confirm()
  // with a proper AlertDialog so the compliance warning is visible.
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchBreaks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/me/academic-breaks`);
      setBreaks(res.data?.breaks || []);
    } catch (_e) {
      toast.error('Failed to load academic breaks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBreaks();
  }, [fetchBreaks]);

  const openAdd = () => {
    setForm({ start_date: '', end_date: '' });
    setConfirmed(false);
    setDialog({ open: true, editing: null });
  };

  const openEdit = (b) => {
    setForm({ start_date: b.start_date, end_date: b.end_date });
    setConfirmed(false);
    setDialog({ open: true, editing: b });
  };

  const close = () => {
    setDialog({ open: false, editing: null });
    setForm({ start_date: '', end_date: '' });
    setConfirmed(false);
  };

  const handleSave = async () => {
    if (!form.start_date || !form.end_date) {
      toast.error('Pick both start and end date');
      return;
    }
    if (form.end_date < form.start_date) {
      toast.error('End date cannot be before start date');
      return;
    }
    if (!confirmed) {
      toast.error('Please confirm the information is accurate');
      return;
    }
    setSaving(true);
    try {
      if (dialog.editing) {
        await axios.put(`${API}/me/academic-breaks/${dialog.editing.id}`, form);
        toast.success('Break updated');
      } else {
        await axios.post(`${API}/me/academic-breaks`, form);
        toast.success('Break added');
      }
      close();
      fetchBreaks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/me/academic-breaks/${deleteTarget.id}`);
      toast.success('Break removed');
      fetchBreaks();
    } catch (_e) {
      toast.error('Delete failed');
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <EmployeeLayout>
      <div className="p-4 sm:p-6 space-y-6 min-h-screen">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-6">
          <div>
            <h1 className="text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
              <GraduationCap className="w-6 h-6 text-primary" strokeWidth={1.5} />
              ACADEMIC BREAKS
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Employees who are students are responsible for declaring all academic breaks and university holiday periods.
              Failure to provide accurate information may affect shift eligibility and compliance with company requirements.
            </p>
          </div>
          <Button
            onClick={openAdd}
            data-testid="add-break-btn"
            className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm uppercase tracking-wider"
          >
            <CalendarPlus className="w-4 h-4 mr-2" />
            New Academic Break
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <p className="text-sm text-muted-foreground p-4">Loading…</p>
        ) : breaks.length === 0 ? (
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-12 text-center space-y-2">
              <GraduationCap className="w-12 h-12 text-muted-foreground mx-auto opacity-50" />
              <p className="text-sm text-muted-foreground">No academic breaks yet.</p>
              <p className="text-xs text-muted-foreground/70">
                Tap &quot;New Academic Break&quot; above to register your first holiday period.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {breaks.map((b, i) => {
              const status = statusFor(b.start_date, b.end_date);
              // Title uses the START year — that's the year the academic
              // break belongs to from a compliance/HR record standpoint.
              const year = (() => {
                try { return format(parseISO(b.start_date), 'yyyy'); }
                catch { return ''; }
              })();
              const days = durationDays(b.start_date, b.end_date);
              return (
                <Card key={b.id} className="bg-card border-border rounded-sm" data-testid={`break-card-${i}`}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-barlow tracking-wider text-sm uppercase">
                        Break #{i + 1}{year && ` (${year})`}
                      </h3>
                      {status.label && (
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-sm ${status.class}`} data-testid={`break-status-${i}`}>
                          {status.label}
                        </span>
                      )}
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-green-400 shrink-0" />
                        <span className="text-muted-foreground">From:</span>
                        <span className="font-mono">{format(parseISO(b.start_date), 'dd MMM yyyy')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-red-400 shrink-0" />
                        <span className="text-muted-foreground">To:</span>
                        <span className="font-mono">{format(parseISO(b.end_date), 'dd MMM yyyy')}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground pl-6">
                        Duration: {days} day{days !== 1 ? 's' : ''}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEdit(b)}
                        className="border-border rounded-sm text-xs"
                        data-testid={`edit-break-${i}`}
                      >
                        <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteTarget(b)}
                        className="border-destructive/30 text-destructive hover:bg-destructive/10 rounded-sm text-xs"
                        data-testid={`delete-break-${i}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Compliance footnote — required by spec. Sits outside the cards so
            it's always visible regardless of how many breaks are listed. */}
        <Card className="bg-muted/20 border-border/60 rounded-sm" data-testid="academic-breaks-footnote">
          <CardContent className="p-4 flex gap-3">
            <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <div className="text-xs text-muted-foreground space-y-2">
              <p>This section is intended for employees undertaking university or college studies.</p>
              <p>It is the employee&apos;s responsibility to keep this information accurate and up to date.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={dialog.open} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-primary" />
              {dialog.editing ? 'Edit Academic Break' : 'New Academic Break'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Start Date</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))}
                className="bg-background border-border rounded-sm"
                data-testid="break-start-input"
                min="2024-01-01"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">End Date</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))}
                className="bg-background border-border rounded-sm"
                data-testid="break-end-input"
                min={form.start_date || todayStr()}
              />
            </div>
            {form.start_date && form.end_date && form.end_date >= form.start_date && (
              <p className="text-xs text-muted-foreground bg-muted/30 p-2 rounded-sm">
                Duration: {durationDays(form.start_date, form.end_date)} day{durationDays(form.start_date, form.end_date) !== 1 ? 's' : ''}
              </p>
            )}

            {/* Mandatory accuracy confirmation — Save button is disabled
                until ticked, AND the click handler re-checks it as a
                belt-and-braces guard for keyboard activation. */}
            <label
              className="flex items-start gap-3 p-3 border border-border rounded-sm bg-muted/10 cursor-pointer"
              data-testid="break-confirm-row"
            >
              <Checkbox
                checked={confirmed}
                onCheckedChange={(v) => setConfirmed(v === true)}
                className="mt-0.5"
                data-testid="break-confirm-checkbox"
              />
              <span className="text-xs leading-relaxed text-foreground">
                I confirm that the information provided is accurate and up to date.
              </span>
            </label>

            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={close}
                disabled={saving}
                className="border-border rounded-sm"
              >
                <XIcon className="w-4 h-4 mr-1.5" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !form.start_date || !form.end_date || !confirmed}
                data-testid="save-break-btn"
                className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm"
              >
                {saving ? 'Saving…' : (dialog.editing ? 'Update' : 'Save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation — surfaces the compliance impact explicitly so
          employees don't blow away records they need to keep on file. */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent className="bg-card border-border rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-barlow tracking-wider uppercase">
              Delete Academic Break?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground space-y-2">
              <span className="block">Deleting this academic break may affect your compliance records.</span>
              <span className="block">Are you sure you want to continue?</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="delete-break-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-sm"
              data-testid="delete-break-confirm"
            >
              Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </EmployeeLayout>
  );
}
